// src/components/layout/Sidebar.tsx
import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Megaphone, BrainCircuit, BarChart3, Building2, ChevronLeft, ChevronRight, DollarSign, Briefcase } from "lucide-react";
import MRNLogo from "@/components/brand/MRNLogo";
import { useAuth } from "@/context/AuthContext";
import type { NavItem, UserRole } from "@/types";
import { clsx } from "clsx";

const NAV_ITEMS: NavItem[] = [
  { id: "dashboard",    label: "Dashboard",       path: "/",            icon: "dashboard",    roles: ["mrn_admin","retailer_admin","retailer_analyst","brand_manager","brand_analyst","agency_user"] },
  { id: "audiences",   label: "Audience Builder", path: "/audiences",   icon: "audiences",    roles: ["mrn_admin","retailer_admin","brand_manager","agency_user"] },
  { id: "campaigns",   label: "Campaigns",        path: "/campaigns",   icon: "campaigns",    roles: ["mrn_admin","brand_manager","brand_analyst","agency_user"] },
  { id: "intelligence",label: "Intelligence",     path: "/intelligence",icon: "intelligence", roles: ["mrn_admin","retailer_admin","retailer_analyst"], badge: "New" },
  { id: "reporting",   label: "Reporting",        path: "/reporting",   icon: "reporting",    roles: ["mrn_admin","retailer_admin","retailer_analyst","brand_manager","brand_analyst","agency_user"] },
  { id: "roi",         label: "ROI Calculator",   path: "/roi-calculator", icon: "roi",        roles: ["mrn_admin","brand_manager","agency_user"] },
  { id: "partners",      label: "Partner Portal", path: "/partners",      icon: "partners",    roles: ["mrn_admin","retailer_admin"] },
  { id: "business-case", label: "Business Case",  path: "/business-case", icon: "briefcase",   roles: ["mrn_admin"] },
];

const ICONS: Record<string, React.ReactNode> = {
  dashboard:    <LayoutDashboard size={18} />,
  audiences:    <Users           size={18} />,
  campaigns:    <Megaphone       size={18} />,
  intelligence: <BrainCircuit    size={18} />,
  reporting:    <BarChart3       size={18} />,
  roi:          <DollarSign      size={18} />,
  partners:     <Building2       size={18} />,
  briefcase:    <Briefcase       size={18} />,
};

interface SidebarProps { collapsed: boolean; onToggle: () => void; }

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { user, can } = useAuth();
  const location = useLocation();
  const visibleItems = NAV_ITEMS.filter(item => can(item.roles as UserRole[]));

  return (
    <aside className={clsx("fixed top-0 left-0 h-screen flex flex-col z-40 transition-all duration-200 bg-dark border-r border-white/5", collapsed ? "w-16" : "w-60")}>
      <div className={clsx("flex items-center h-14 border-b border-white/5 flex-shrink-0", collapsed ? "justify-center px-0" : "px-4")}>
        {collapsed
          ? <MRNLogo variant="mark" size={32} />
          : <MRNLogo variant="sidebar" onDark={true} />
        }
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {!collapsed && <p className="text-white/30 text-2xs font-semibold uppercase tracking-widest px-2 mb-2">Platform</p>}
        <ul className="space-y-0.5">
          {visibleItems.map(item => {
            const isActive = item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path);
            return (
              <li key={item.id}>
                <NavLink to={item.path}
                  className={clsx("flex items-center rounded-md text-sm font-medium transition-all duration-150 group", collapsed ? "justify-center px-0 py-2.5 w-full" : "gap-3 px-3 py-2", isActive ? "bg-blue text-white" : "text-white/50 hover:text-white hover:bg-white/8")}
                  title={collapsed ? item.label : undefined}>
                  <span className={clsx("flex-shrink-0", isActive ? "text-white" : "text-white/50 group-hover:text-white")}>
                    {ICONS[item.icon]}
                  </span>
                  {!collapsed && (
                    <>
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge && <span className="text-2xs font-semibold bg-cyan/20 text-cyan px-1.5 py-0.5 rounded-full">{item.badge}</span>}
                    </>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
      {user && (
        <div className={clsx("border-t border-white/5 flex-shrink-0", collapsed ? "p-2 flex justify-center" : "p-3")}>
          {collapsed ? (
            <div className="w-8 h-8 rounded-full bg-blue/30 flex items-center justify-center">
              <span className="text-white text-xs font-semibold">{user.avatarInitials}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-blue/30 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-semibold">{user.avatarInitials}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-xs font-medium truncate">{user.name}</div>
                <div className="text-white/40 text-2xs truncate">{user.orgName}</div>
              </div>
            </div>
          )}
        </div>
      )}
      <button onClick={onToggle}
        className="absolute -right-3 top-16 w-6 h-6 rounded-full bg-dark border border-white/10 flex items-center justify-center text-white/50 hover:text-white hover:border-white/30 transition-all duration-150 shadow-md z-50"
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}
